import { useState } from 'react';
import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { hasPermission } from '../../../core/permissions';
import { formatMilitaryId } from '../../../core/military-id';
import {
  EXTERNAL_COMPANIES,
  HOME_COMPANY,
  normalizeCompany,
} from '../../../core/vehicle-location';
import { createVehicle, updateVehicle } from '../../../db/fleet-store';
import {
  VEHICLE_CATEGORIES,
  VEHICLE_CATEGORY_LABELS,
  VEHICLE_STATUSES,
  VEHICLE_STATUS_LABELS,
  VEHICLE_TYPES,
  VEHICLE_TYPE_LABELS,
  WEIGHT_CATEGORIES,
  WEIGHT_CATEGORY_LABELS,
} from '../../../types/fleet';
import type {
  Vehicle,
  VehicleCategory,
  VehicleStatus,
  VehicleType,
  WeightCategory,
} from '../../../types/fleet';
import type { User } from '../../../types/user';

interface VehicleFormModalProps {
  user: User;
  /** `null` opens the form empty, for a new vehicle. */
  vehicle: Vehicle | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const fieldClass =
  'w-full h-10 rounded-xl border border-line bg-canvas px-3 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel';

const DEFAULT_COMPANY = 'פלוגת שינוע 99';

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

/**
 * Add / edit a vehicle. `MANAGE_VEHICLES` is checked here so the screen never
 * offers an action it cannot complete, and again inside the store — the store
 * guard is the one that actually protects the data.
 */
export function VehicleFormModal({ user, vehicle, onClose, onSaved }: VehicleFormModalProps) {
  const canManage = hasPermission(user, 'MANAGE_VEHICLES');

  const [militaryId, setMilitaryId] = useState(vehicle?.militaryId ?? '');
  const [assignedCompany, setAssignedCompany] = useState(
    normalizeCompany(vehicle?.assignedCompany) || HOME_COMPANY,
  );
  const [plateNumber, setPlateNumber] = useState(vehicle?.plateNumber ?? '');
  const [name, setName] = useState(vehicle?.name ?? '');
  const [model, setModel] = useState(vehicle?.model ?? '');
  const [vehicleType, setVehicleType] = useState<VehicleType>(vehicle?.vehicleType ?? 'truck');
  const [category, setCategory] = useState<VehicleCategory>(vehicle?.category ?? 'yaram');
  const [weightCategory, setWeightCategory] = useState<WeightCategory>(
    vehicle?.weightCategory ?? 'medium',
  );
  const [status, setStatus] = useState<VehicleStatus>(vehicle?.status ?? 'available');
  const [notes, setNotes] = useState(vehicle?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!canManage) {
    return (
      <Modal title="אין הרשאה" onClose={onClose}>
        <div className="text-center py-6">
          <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <Lock size={22} aria-hidden />
          </div>
          <p className="text-sm text-ink/70">
            נדרשת הרשאת ניהול רכבים (MANAGE_VEHICLES) כדי להוסיף או לערוך רכב.
          </p>
        </div>
      </Modal>
    );
  }

  const handleSubmit = async () => {
    setError(null);
    // The internal number is optional: the pool the company handed over came
    // with plates and no internal numbers, and a vehicle is identified by its
    // מספר צ until the unit paints one in. A number that *is* typed still has
    // to be a real one.
    const typedId = militaryId.trim();
    const canonicalId = typedId ? formatMilitaryId(typedId) : '';
    if (canonicalId === null) {
      setError('מספר פנימי חייב להיות בן 1–3 ספרות.');
      return;
    }
    if (!name.trim() || !plateNumber.trim()) {
      setError('שם הרכב ומספר צ מלא הם שדות חובה.');
      return;
    }

    const draft = {
      militaryId: canonicalId,
      assignedCompany: normalizeCompany(assignedCompany) || HOME_COMPANY,
      plateNumber: plateNumber.trim(),
      name: name.trim(),
      model: model.trim() || name.trim(),
      vehicleType,
      category,
      weightCategory,
      status,
      disallowedSoldierIds: vehicle?.disallowedSoldierIds ?? [],
      company: vehicle?.company ?? DEFAULT_COMPANY,
      notes: notes.trim() || undefined,
    };

    setSaving(true);
    try {
      if (vehicle) await updateVehicle(user, vehicle.id, draft);
      else await createVehicle(user, draft);
      await onSaved();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(
        /constraint/i.test(message) ? 'מספר פנימי זה כבר קיים במערכת.' : `השמירה נכשלה — ${message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={vehicle ? `עריכת רכב ${vehicle.militaryId || vehicle.plateNumber}` : 'הוספת רכב'}
      subtitle={vehicle ? vehicle.name : 'הזינו את פרטי הרכב החדש'}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'שומר…' : 'שמירה'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Labelled label="מספר פנימי (3 ספרות, לא חובה)">
            <input
              value={militaryId}
              onChange={(event) => setMilitaryId(event.target.value)}
              inputMode="numeric"
              maxLength={3}
              placeholder="טרם נרשם"
              data-dir="ltr"
              className={fieldClass}
            />
          </Labelled>
          <Labelled label="מספר צ מלא">
            <input
              value={plateNumber}
              onChange={(event) => setPlateNumber(event.target.value)}
              placeholder="צ 12-345-67"
              className={fieldClass}
            />
          </Labelled>
        </div>

        <Labelled label="שם הרכב">
          <input value={name} onChange={(event) => setName(event.target.value)} className={fieldClass} />
        </Labelled>

        <Labelled label="דגם">
          <input
            value={model}
            onChange={(event) => setModel(event.target.value)}
            className={fieldClass}
          />
        </Labelled>

        {/* Where the vehicle stands. The badge on the card follows this field —
            שינוע is 🟢, any other company is 🟠. */}
        <Labelled label="מיקום — הפלוגה המחזיקה ברכב">
          <input
            value={assignedCompany}
            onChange={(event) => setAssignedCompany(event.target.value)}
            list="vehicle-companies"
            placeholder={HOME_COMPANY}
            className={fieldClass}
          />
        </Labelled>
        <datalist id="vehicle-companies">
          <option value={HOME_COMPANY} />
          {EXTERNAL_COMPANIES.map((company) => (
            <option key={company} value={company} />
          ))}
        </datalist>

        <div className="grid grid-cols-2 gap-3">
          <Labelled label="סוג רכב">
            <select
              value={vehicleType}
              onChange={(event) => setVehicleType(event.target.value as VehicleType)}
              className={fieldClass}
            >
              {VEHICLE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {VEHICLE_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </Labelled>
          <Labelled label="קטגוריה">
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value as VehicleCategory)}
              className={fieldClass}
            >
              {VEHICLE_CATEGORIES.map((option) => (
                <option key={option} value={option}>
                  {VEHICLE_CATEGORY_LABELS[option]}
                </option>
              ))}
            </select>
          </Labelled>
          <Labelled label="דרגת משקל נדרשת">
            <select
              value={weightCategory}
              onChange={(event) => setWeightCategory(event.target.value as WeightCategory)}
              className={fieldClass}
            >
              {WEIGHT_CATEGORIES.map((option) => (
                <option key={option} value={option}>
                  {WEIGHT_CATEGORY_LABELS[option]}
                </option>
              ))}
            </select>
          </Labelled>
          <Labelled label="סטטוס">
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as VehicleStatus)}
              className={fieldClass}
            >
              {VEHICLE_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {VEHICLE_STATUS_LABELS[option]}
                </option>
              ))}
            </select>
          </Labelled>
        </div>

        <Labelled label="הערות">
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            className="w-full rounded-xl border border-line bg-canvas p-3 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel"
          />
        </Labelled>

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
